import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Bot,
  ChevronDown,
  Mail,
  Pencil,
  Plus,
  Send,
  Trash2,
  Webhook,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"

import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { PermissionButton } from "@/components/Auth"
import {
  DestinationFormFields,
  DestinationSelector,
  DestinationStatusBadge,
  NotificationConfirmDialog,
} from "@/components/Notifications"
import { LoadingPage } from "@/components/System"
import { useToast } from "@/hooks"
import {
  notificationDestinationResponseToDestination,
  agentResponseToAgent,
  workspaceAgentHandoffNotificationResponseToSetting,
  useCreateNotificationDestinationApi,
  useCreateNotificationRuleApi,
  useDeleteWorkspaceAgentHandoffNotificationSettingApi,
  useDeleteNotificationDestinationApi,
  useDeleteNotificationRuleApi,
  useGetNotificationDestinationsApi,
  useGetNotificationEventsApi,
  useGetPublishedAgentsApi,
  useGetWorkspaceAgentHandoffNotificationSettingsApi,
  useSendNotificationDestinationVerificationApi,
  useTestNotificationDestinationApi,
  useUpdateNotificationDestinationApi,
  useUpdateNotificationRuleApi,
  useUpdateWorkspaceAgentHandoffNotificationSettingApi,
} from "@/hooks/api"
import type {
  HandoffNotificationOverrideMode,
  NotificationDestination,
  NotificationEventSchema,
  NotificationRuleSchema,
  WorkspaceAgentHandoffNotificationSetting,
} from "@/hooks/api/notifications"
import {
  getUserFacingApiErrorToastProps,
  normalizeApiError,
} from "@/lib/apiErrors"
import { hasPermission } from "@/lib/utils"
import { Permission, Workspace } from "@/types"

export interface NotificationsTabProps {
  workspace: Workspace
}

interface DestinationDraft {
  id?: string
  name: string
  destinationType: string
  originalDestinationType?: string
  endpoint: string
  usedBy: NotificationDestination["usedBy"]
}

interface RuleDraft {
  id?: string
  eventType: string
  name: string
  agentId: string
  schemaId: string
  isEnabled: boolean
  destinationIds: string[]
  requiresCondition: boolean
}

interface AgentHandoffSettingDraft extends Omit<
  WorkspaceAgentHandoffNotificationSetting,
  "mode"
> {
  mode: HandoffNotificationOverrideMode
  isNew: boolean
}

interface FormSchemaOption {
  agentId: string
  agentName: string
  schemaId: string
  title: string
}

interface ConfirmationRequest {
  title: string
  description: string
  confirmText: string
  destructive?: boolean
  onConfirm: () => void
}

const EMPTY_DESTINATION_DRAFT: DestinationDraft = {
  name: "",
  destinationType: "slack_webhook",
  endpoint: "",
  usedBy: [],
}

const AGENT_HANDOFF_DEFAULT_RULE_NAME = "Agent handoff default"
const FORM_REQUEST_DEFAULT_RULE_NAME = "Form request submitted notifications"

const formScopeKey = (agentId: string, schemaId: string) =>
  JSON.stringify([agentId, schemaId])

const formSchemaOptionName = (option: FormSchemaOption) =>
  `${option.title} · ${option.agentName}`

const formScopeFallbackName = (agentId: string, schemaId: string) =>
  `${schemaId} · ${agentId}`

const formRuleScope = (conditions: Record<string, unknown> | null) => {
  const agentId =
    typeof conditions?.agent_id === "string" ? conditions.agent_id : ""
  const schemaId =
    typeof conditions?.schema_id === "string" ? conditions.schema_id : ""
  return agentId && schemaId ? { agentId, schemaId } : null
}

const internalFormRequestRuleName = (agentId: string, schemaId: string) =>
  agentId && schemaId
    ? `Form request: ${agentId}/${schemaId}`
    : FORM_REQUEST_DEFAULT_RULE_NAME

const agentHandoffSettingToDraft = (
  setting: WorkspaceAgentHandoffNotificationSetting
): AgentHandoffSettingDraft => ({
  ...setting,
  mode: setting.mode === "disabled" ? "disabled" : "custom",
  destinationIds:
    setting.mode === "workspace_default" ? [] : setting.destinationIds,
  isNew: setting.mode === "workspace_default",
})

const emptyAgentHandoffSettingDraft = (
  allowedDestinationTypes: string[]
): AgentHandoffSettingDraft => ({
  agentId: "",
  agentName: "",
  mode: "custom",
  destinationIds: [],
  effectiveDestinationIds: [],
  workspaceDefaultDestinationIds: [],
  workspaceDefaultAvailable: false,
  allowedDestinationTypes,
  isNew: true,
})

const destinationEndpointPlaceholder = (destinationType: string) => {
  if (destinationType === "email") return "name@example.com"
  if (destinationType === "slack_workflow_webhook") {
    return "https://hooks.slack.com/triggers/..."
  }
  return "https://hooks.slack.com/services/..."
}

const destinationConfig = (
  destinationType: string,
  endpoint: string
): Record<string, string> =>
  destinationType === "email"
    ? { email: endpoint.trim() }
    : { webhook_url: endpoint.trim() }

const haveSameIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id) => right.includes(id))

const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
}

const NotificationsTab = ({ workspace }: NotificationsTabProps) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { alertToast, successToast, warningToast } = useToast()
  const organizationId = workspace.organizationId || ""
  const canEdit = hasPermission(
    workspace.myPermissions,
    Permission.WsUpdateResources
  )
  const [destinations, setDestinations] = useState<NotificationDestination[]>(
    []
  )
  const [events, setEvents] = useState<NotificationEventSchema[]>([])
  const [agentHandoffSettings, setAgentHandoffSettings] = useState<
    WorkspaceAgentHandoffNotificationSetting[]
  >([])
  const [formSchemaOptions, setFormSchemaOptions] = useState<
    FormSchemaOption[]
  >([])
  const [isLoading, setIsLoading] = useState(true)
  const [destinationDraft, setDestinationDraft] =
    useState<DestinationDraft | null>(null)
  const destinationTypeChanged = Boolean(
    destinationDraft?.id &&
    destinationDraft.originalDestinationType !==
      destinationDraft.destinationType
  )
  const destinationEndpointRequired = Boolean(
    destinationDraft && (!destinationDraft.id || destinationTypeChanged)
  )
  const destinationDraftIsValid = Boolean(
    destinationDraft?.name.trim() &&
    (!destinationEndpointRequired || destinationDraft.endpoint.trim())
  )
  const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null)
  const [agentHandoffSettingDraft, setAgentHandoffSettingDraft] =
    useState<AgentHandoffSettingDraft | null>(null)
  const [confirmationRequest, setConfirmationRequest] =
    useState<ConfirmationRequest | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [updatingDestinationIds, setUpdatingDestinationIds] = useState<
    Set<string>
  >(new Set())
  const [runningDestinationActionIds, setRunningDestinationActionIds] =
    useState<Set<string>>(new Set())
  const [verificationClock, setVerificationClock] = useState(() => Date.now())
  const [updatingRuleIds, setUpdatingRuleIds] = useState<Set<string>>(new Set())
  const [updatingAgentSettingIds, setUpdatingAgentSettingIds] = useState<
    Set<string>
  >(new Set())
  const [highlightedAgentId, setHighlightedAgentId] = useState<string | null>(
    null
  )
  const focusedAgentKeyRef = useRef<string | null>(null)
  const agentHighlightTimeoutRef = useRef<number | null>(null)
  const verificationRefreshInFlightRef = useRef(false)
  const focusedEventType = searchParams.get("event")
  const focusedAgentId = searchParams.get("agent")
  const hasActiveVerificationCooldown = destinations.some(
    (destination) =>
      destination.destinationType === "email" &&
      destination.verificationStatus !== "verified" &&
      destination.verificationResendAvailableAt !== null &&
      destination.verificationResendAvailableAt > verificationClock
  )

  const { fetchFn: fetchDestinations } = useGetNotificationDestinationsApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: fetchEvents } = useGetNotificationEventsApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: fetchAgentHandoffSettings } =
    useGetWorkspaceAgentHandoffNotificationSettingsApi(
      organizationId,
      workspace.id
    )
  const { fetchFn: fetchPublishedAgents } = useGetPublishedAgentsApi()
  const { fetchFn: createDestination } = useCreateNotificationDestinationApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: updateDestination } = useUpdateNotificationDestinationApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: deleteDestination } = useDeleteNotificationDestinationApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: testDestination } = useTestNotificationDestinationApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: sendVerification } =
    useSendNotificationDestinationVerificationApi(organizationId, workspace.id)
  const { fetchFn: createRule } = useCreateNotificationRuleApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: updateRule } = useUpdateNotificationRuleApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: deleteRule } = useDeleteNotificationRuleApi(
    organizationId,
    workspace.id
  )
  const { fetchFn: updateAgentHandoffSetting } =
    useUpdateWorkspaceAgentHandoffNotificationSettingApi(
      organizationId,
      workspace.id
    )
  const { fetchFn: deleteAgentHandoffSetting } =
    useDeleteWorkspaceAgentHandoffNotificationSettingApi(
      organizationId,
      workspace.id
    )

  const systemRuleDisplayName = (eventType: string) => {
    if (eventType === "agent_handoff.triggered") {
      return t("workspace.notifications.rules.systemNames.agentHandoff")
    }
    if (eventType === "form_request.submitted") {
      return t("workspace.notifications.rules.systemNames.formRequest")
    }
    return null
  }

  const ruleDisplayName = (rule: NotificationRuleSchema) =>
    rule.conditions === null
      ? systemRuleDisplayName(rule.event_type) || rule.name
      : rule.name

  const ruleReferenceDisplayName = (
    rule: NotificationDestination["usedBy"][number]
  ) => {
    if (rule.event_type === "agent_handoff.triggered") {
      if (rule.agent_name) {
        return t("workspace.notifications.rules.agentReference", {
          agent: rule.agent_name,
        })
      }
      return (
        systemRuleDisplayName("agent_handoff.triggered") ||
        AGENT_HANDOFF_DEFAULT_RULE_NAME
      )
    }
    if (rule.event_type === "form_request.submitted") {
      const scope = formRuleScope(rule.conditions)
      if (scope) {
        const option = formSchemaOptions.find(
          (schema) =>
            schema.agentId === scope.agentId &&
            schema.schemaId === scope.schemaId
        )
        return t("workspace.notifications.rules.schemaCondition", {
          formName: option
            ? formSchemaOptionName(option)
            : formScopeFallbackName(scope.agentId, scope.schemaId),
        })
      }
      return (
        systemRuleDisplayName("form_request.submitted") ||
        FORM_REQUEST_DEFAULT_RULE_NAME
      )
    }
    return rule.name
  }

  const ruleReferenceList = (
    rules: NotificationDestination["usedBy"]
  ): string => rules.map(ruleReferenceDisplayName).join(", ")

  const showError = useCallback(
    (error: unknown, fallback: string, warningFallback = fallback) => {
      const normalizedError = normalizeApiError(error)
      if (
        normalizedError.kind !== "permission_denied" &&
        (normalizedError.status === 400 || normalizedError.status === 409)
      ) {
        warningToast({
          title: t("workspace.notifications.warnings.title"),
          description: warningFallback,
        })
        return
      }
      alertToast(
        getUserFacingApiErrorToastProps(error, t, {
          title: t("workspace.notifications.errors.title"),
          fallbackDescription: fallback,
        })
      )
    },
    [alertToast, t, warningToast]
  )
  const showErrorRef = useRef(showError)
  showErrorRef.current = showError
  const translationRef = useRef(t)
  translationRef.current = t

  const refreshDestinationVerificationStatus = useCallback(async () => {
    if (verificationRefreshInFlightRef.current) return
    verificationRefreshInFlightRef.current = true
    try {
      const response = await fetchDestinations()
      setDestinations(
        (response?.data || []).map(notificationDestinationResponseToDestination)
      )
      setVerificationClock(Date.now())
    } finally {
      verificationRefreshInFlightRef.current = false
    }
  }, [fetchDestinations])

  const refresh = useCallback(async () => {
    if (!organizationId) return
    try {
      const [destinationResult, eventResult, agentSettingResult, agentResult] =
        await Promise.allSettled([
          fetchDestinations(),
          fetchEvents(),
          fetchAgentHandoffSettings(),
          fetchPublishedAgents({
            searchParams: new URLSearchParams({
              oid: organizationId,
              wid: workspace.id,
            }),
          }),
        ])

      const loadErrors: unknown[] = []
      if (destinationResult.status === "fulfilled") {
        setDestinations(
          (destinationResult.value?.data || []).map(
            notificationDestinationResponseToDestination
          )
        )
      } else {
        loadErrors.push(destinationResult.reason)
      }

      if (eventResult.status === "fulfilled") {
        setEvents(eventResult.value?.data || [])
      } else {
        loadErrors.push(eventResult.reason)
      }

      if (agentSettingResult.status === "fulfilled") {
        setAgentHandoffSettings(
          (agentSettingResult.value?.data || []).map(
            workspaceAgentHandoffNotificationResponseToSetting
          )
        )
      } else {
        loadErrors.push(agentSettingResult.reason)
      }

      if (agentResult.status === "fulfilled") {
        const configuredForms = new Map<string, FormSchemaOption>()
        const workspaceAgents = (agentResult.value?.data || []).map(
          agentResponseToAgent
        )
        workspaceAgents.forEach((agent) => {
          agent.unifiedTools.forEach((tool) => {
            const schema = tool.customFormSchema
            if (tool.type !== "request_form" || !schema?.id) return
            configuredForms.set(formScopeKey(agent.id, schema.id), {
              agentId: agent.id,
              agentName: agent.name || agent.id,
              schemaId: schema.id,
              title: schema.title || schema.id,
            })
          })
        })
        setFormSchemaOptions(
          Array.from(configuredForms.values()).sort(
            (left, right) =>
              left.title.localeCompare(right.title) ||
              left.agentName.localeCompare(right.agentName)
          )
        )
      } else {
        loadErrors.push(agentResult.reason)
      }

      const loadError =
        loadErrors.find(
          (error) => normalizeApiError(error).kind === "permission_denied"
        ) || loadErrors[0]
      if (loadError) {
        showErrorRef.current(
          loadError,
          translationRef.current("workspace.notifications.errors.load")
        )
      }
    } catch (error) {
      showErrorRef.current(
        error,
        translationRef.current("workspace.notifications.errors.load")
      )
    } finally {
      setIsLoading(false)
    }
  }, [
    fetchAgentHandoffSettings,
    fetchPublishedAgents,
    fetchDestinations,
    fetchEvents,
    organizationId,
    workspace.id,
  ])

  useEffect(() => {
    setIsLoading(true)
    refresh()
  }, [refresh, workspace.id])

  useEffect(() => {
    if (!hasActiveVerificationCooldown) return
    const intervalId = window.setInterval(() => {
      setVerificationClock(Date.now())
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [hasActiveVerificationCooldown])

  useEffect(() => {
    const cooldownReachedZero = destinations.some(
      (destination) =>
        destination.destinationType === "email" &&
        destination.verificationStatus !== "verified" &&
        destination.verificationResendAvailableAt !== null &&
        destination.verificationResendAvailableAt <= verificationClock
    )
    if (!cooldownReachedZero) return

    void refreshDestinationVerificationStatus().catch(() => undefined)
  }, [destinations, refreshDestinationVerificationStatus, verificationClock])

  useEffect(() => {
    const hasPendingEmailDestination = destinations.some(
      (destination) =>
        destination.destinationType === "email" &&
        destination.verificationStatus !== "verified"
    )
    if (!hasPendingEmailDestination) return

    const refreshVerificationStatus = () => {
      void refreshDestinationVerificationStatus().catch(() => undefined)
    }
    window.addEventListener("focus", refreshVerificationStatus)
    return () => window.removeEventListener("focus", refreshVerificationStatus)
  }, [destinations, refreshDestinationVerificationStatus])

  useEffect(() => {
    return () => {
      if (agentHighlightTimeoutRef.current !== null) {
        window.clearTimeout(agentHighlightTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (focusedEventType !== "agent_handoff.triggered" || !focusedAgentId) {
      return
    }
    const setting = agentHandoffSettings.find(
      (candidate) => candidate.agentId === focusedAgentId
    )
    if (!setting) return

    const focusedAgentKey = `${workspace.id}:${focusedAgentId}`
    if (focusedAgentKeyRef.current === focusedAgentKey) return

    focusedAgentKeyRef.current = focusedAgentKey
    setHighlightedAgentId(focusedAgentId)
    if (canEdit) {
      setAgentHandoffSettingDraft(agentHandoffSettingToDraft(setting))
    }
    if (agentHighlightTimeoutRef.current !== null) {
      window.clearTimeout(agentHighlightTimeoutRef.current)
    }
    agentHighlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedAgentId((current) =>
        current === focusedAgentId ? null : current
      )
      agentHighlightTimeoutRef.current = null
    }, 1800)
  }, [
    agentHandoffSettings,
    canEdit,
    focusedAgentId,
    focusedEventType,
    workspace.id,
  ])

  const saveDestination = async (acknowledgeImpact = false) => {
    if (!destinationDraft || !canEdit) return
    const name = destinationDraft.name.trim()
    const endpoint = destinationDraft.endpoint.trim()
    if (!name || (destinationEndpointRequired && !endpoint)) return
    const changesSharedEndpoint =
      Boolean(destinationDraft.id) &&
      Boolean(endpoint) &&
      destinationDraft.usedBy.length > 1
    if (changesSharedEndpoint && !acknowledgeImpact) {
      setConfirmationRequest({
        title: t(
          "workspace.notifications.destinations.sharedImpactConfirmTitle"
        ),
        description: t(
          "workspace.notifications.destinations.sharedImpactConfirm",
          {
            rules: ruleReferenceList(destinationDraft.usedBy),
          }
        ),
        confirmText: t(
          "workspace.notifications.destinations.sharedImpactConfirmAction"
        ),
        onConfirm: () => {
          void saveDestination(true)
        },
      })
      return
    }
    setIsSaving(true)
    let createdEmailAddress: string | null = null
    try {
      if (destinationDraft.id) {
        const changesEndpoint = Boolean(endpoint)
        await updateDestination({
          urlBuilderParams: { destinationId: destinationDraft.id },
          jsonBody: {
            name,
            ...(changesEndpoint
              ? {
                  destination_type: destinationDraft.destinationType,
                  config: destinationConfig(
                    destinationDraft.destinationType,
                    endpoint
                  ),
                  acknowledge_impact: acknowledgeImpact,
                }
              : {}),
          },
        })
      } else {
        await createDestination({
          jsonBody: {
            name,
            destination_type: destinationDraft.destinationType,
            config: destinationConfig(
              destinationDraft.destinationType,
              endpoint
            ),
          },
        })
        if (destinationDraft.destinationType === "email") {
          createdEmailAddress = endpoint
        }
      }
      setDestinationDraft(null)
      successToast(
        createdEmailAddress
          ? {
              title: t("workspace.notifications.destinations.emailCreated"),
              description: t(
                "workspace.notifications.destinations.emailCreatedDescription",
                { email: createdEmailAddress }
              ),
            }
          : {
              title: t("workspace.notifications.destinations.saved"),
            }
      )
      await refresh()
    } catch (error) {
      showError(error, t("workspace.notifications.errors.saveDestination"))
    } finally {
      setIsSaving(false)
    }
  }

  const updateDestinationEnabled = async (
    destination: NotificationDestination,
    acknowledgeImpact = false
  ) => {
    if (!canEdit || updatingDestinationIds.has(destination.id)) return
    if (destination.usedBy.length > 1 && !acknowledgeImpact) {
      setConfirmationRequest({
        title: t(
          "workspace.notifications.destinations.sharedImpactConfirmTitle"
        ),
        description: t(
          "workspace.notifications.destinations.sharedImpactConfirm",
          {
            rules: ruleReferenceList(destination.usedBy),
          }
        ),
        confirmText: t(
          "workspace.notifications.destinations.sharedImpactConfirmAction"
        ),
        onConfirm: () => {
          void updateDestinationEnabled(destination, true)
        },
      })
      return
    }
    const isEnabled = !destination.isEnabled
    setUpdatingDestinationIds((current) => new Set(current).add(destination.id))
    try {
      await updateDestination({
        urlBuilderParams: { destinationId: destination.id },
        jsonBody: {
          is_enabled: isEnabled,
          acknowledge_impact: destination.usedBy.length > 1,
        },
      })
      await refresh()
      successToast({
        title: t(
          isEnabled
            ? "workspace.notifications.destinations.enabledSuccess"
            : "workspace.notifications.destinations.disabledSuccess"
        ),
      })
    } catch (error) {
      showError(error, t("workspace.notifications.errors.saveDestination"))
    } finally {
      setUpdatingDestinationIds((current) => {
        const next = new Set(current)
        next.delete(destination.id)
        return next
      })
    }
  }

  const removeDestination = (destination: NotificationDestination) => {
    if (!canEdit) return
    setConfirmationRequest({
      title: t("workspace.notifications.destinations.deleteNamed", {
        name: destination.name,
      }),
      description: t("workspace.notifications.destinations.deleteConfirm", {
        name: destination.name,
      }),
      confirmText: t(
        "workspace.notifications.destinations.deleteConfirmAction"
      ),
      destructive: true,
      onConfirm: () => {
        void deleteDestinationConfirmed(destination)
      },
    })
  }

  const deleteDestinationConfirmed = async (
    destination: NotificationDestination
  ) => {
    try {
      await deleteDestination({
        urlBuilderParams: { destinationId: destination.id },
      })
      successToast({
        title: t("workspace.notifications.destinations.deleted"),
      })
      await refresh()
    } catch (error) {
      showError(error, t("workspace.notifications.errors.deleteDestination"))
    }
  }

  const runDestinationAction = async (
    destination: NotificationDestination,
    action: "test" | "verify"
  ) => {
    if (!canEdit || runningDestinationActionIds.has(destination.id)) return
    setRunningDestinationActionIds((current) =>
      new Set(current).add(destination.id)
    )
    try {
      if (action === "verify") {
        await sendVerification({
          urlBuilderParams: { destinationId: destination.id },
        })
      } else {
        await testDestination({
          urlBuilderParams: { destinationId: destination.id },
        })
      }
      successToast({
        title:
          action === "verify"
            ? t("workspace.notifications.destinations.verificationSent")
            : t("workspace.notifications.destinations.testQueued"),
      })
      await refresh()
    } catch (error) {
      showError(error, t("workspace.notifications.errors.destinationAction"))
    } finally {
      setRunningDestinationActionIds((current) => {
        const next = new Set(current)
        next.delete(destination.id)
        return next
      })
    }
  }

  const openRuleEditor = (
    event: NotificationEventSchema,
    rule?: NotificationRuleSchema,
    requiresCondition = false
  ) => {
    const scope = formRuleScope(rule?.conditions || null)
    const isConditionalRule = requiresCondition || scope !== null
    setRuleDraft({
      id: rule?.id,
      eventType: event.event_type,
      name:
        rule?.name ||
        (!isConditionalRule && event.event_type === "agent_handoff.triggered"
          ? AGENT_HANDOFF_DEFAULT_RULE_NAME
          : !isConditionalRule && event.event_type === "form_request.submitted"
            ? FORM_REQUEST_DEFAULT_RULE_NAME
            : t(
                `workspace.notifications.events.${event.event_type}.defaultRuleName`
              )),
      agentId: scope?.agentId || "",
      schemaId: scope?.schemaId || "",
      isEnabled: rule?.is_enabled ?? true,
      destinationIds: rule?.destination_ids || [],
      requiresCondition: isConditionalRule,
    })
  }

  const selectedRuleEvent = events.find(
    (event) => event.event_type === ruleDraft?.eventType
  )
  const destinationMap = useMemo(
    () =>
      new Map(destinations.map((destination) => [destination.id, destination])),
    [destinations]
  )
  const formSchemaOptionMap = useMemo(
    () =>
      new Map(
        formSchemaOptions.map((schema) => [
          formScopeKey(schema.agentId, schema.schemaId),
          schema,
        ])
      ),
    [formSchemaOptions]
  )
  const formRequestRules = useMemo(
    () =>
      events.find((event) => event.event_type === "form_request.submitted")
        ?.rules || [],
    [events]
  )
  const configuredFormScopeKeys = useMemo(
    () =>
      new Set(
        formRequestRules.flatMap((rule) => {
          const scope = formRuleScope(rule.conditions)
          return scope ? [formScopeKey(scope.agentId, scope.schemaId)] : []
        })
      ),
    [formRequestRules]
  )
  const availableNewFormSchemaOptions = formSchemaOptions.filter(
    (schema) =>
      !configuredFormScopeKeys.has(
        formScopeKey(schema.agentId, schema.schemaId)
      )
  )
  const compatibleRuleDestinations = destinations.filter((destination) =>
    selectedRuleEvent?.allowed_destination_types.includes(
      destination.destinationType
    )
  )
  const selectedRuleDestinations = (ruleDraft?.destinationIds || []).flatMap(
    (id) => {
      const destination = destinationMap.get(id)
      return destination ? [destination] : []
    }
  )
  const enabledRuleHasNoSelectedDestination = Boolean(
    ruleDraft?.isEnabled && ruleDraft.destinationIds.length === 0
  )
  const enabledRuleHasNoReadyDestination = Boolean(
    ruleDraft?.isEnabled &&
    ruleDraft.destinationIds.length > 0 &&
    !selectedRuleDestinations.some((destination) => destination.isReady)
  )
  const hasUnavailableSelectedDestination =
    selectedRuleDestinations.some((destination) => !destination.isReady) &&
    !enabledRuleHasNoReadyDestination
  const hasUnconditionalRuleConflict = Boolean(
    !ruleDraft?.requiresCondition &&
    ruleDraft?.isEnabled &&
    !ruleDraft.schemaId.trim() &&
    selectedRuleEvent?.rules.some(
      (rule) =>
        rule.id !== ruleDraft.id && rule.is_enabled && rule.conditions === null
    )
  )
  const conditionalRuleRequiresSchema = Boolean(
    ruleDraft?.requiresCondition &&
    (!ruleDraft.agentId?.trim() || !ruleDraft.schemaId.trim())
  )
  const originalRule = ruleDraft?.id
    ? selectedRuleEvent?.rules.find((rule) => rule.id === ruleDraft.id)
    : undefined
  const isSystemWorkspaceRuleDraft = Boolean(
    ruleDraft &&
    !ruleDraft.requiresCondition &&
    !ruleDraft.schemaId.trim() &&
    (!ruleDraft.id || originalRule?.conditions === null) &&
    systemRuleDisplayName(ruleDraft.eventType)
  )
  const isFormIndividualRuleDraft = Boolean(
    ruleDraft?.eventType === "form_request.submitted" &&
    ruleDraft.requiresCondition
  )
  const isFormIndividualRuleCustom = Boolean(
    isFormIndividualRuleDraft && ruleDraft?.isEnabled
  )
  const ruleDraftFormName = ruleDraft
    ? (() => {
        const option = formSchemaOptionMap.get(
          formScopeKey(ruleDraft.agentId, ruleDraft.schemaId)
        )
        return option
          ? formSchemaOptionName(option)
          : formScopeFallbackName(ruleDraft.agentId, ruleDraft.schemaId)
      })()
    : ""
  const isRuleDraftDirty = Boolean(
    ruleDraft &&
    (!ruleDraft.id ||
      !originalRule ||
      ruleDraft.name.trim() !== originalRule.name ||
      (ruleDraft.agentId || "").trim() !==
        (typeof originalRule.conditions?.agent_id === "string"
          ? originalRule.conditions.agent_id
          : "") ||
      ruleDraft.schemaId.trim() !==
        (typeof originalRule.conditions?.schema_id === "string"
          ? originalRule.conditions.schema_id
          : "") ||
      ruleDraft.isEnabled !== originalRule.is_enabled ||
      !haveSameIds(ruleDraft.destinationIds, originalRule.destination_ids))
  )
  const handoffDestinations = destinations.filter((destination) => {
    const allowedTypes = agentHandoffSettingDraft?.allowedDestinationTypes || []
    return (
      allowedTypes.length === 0 ||
      allowedTypes.includes(destination.destinationType)
    )
  })
  const selectedAgentHandoffDestinations = (
    agentHandoffSettingDraft?.destinationIds || []
  ).flatMap((id) => {
    const destination = destinationMap.get(id)
    return destination ? [destination] : []
  })
  const agentCustomHasNoReadyDestination = Boolean(
    agentHandoffSettingDraft?.mode === "custom" &&
    !selectedAgentHandoffDestinations.some((destination) => destination.isReady)
  )
  const agentHandoffRequiresAgent = Boolean(
    agentHandoffSettingDraft?.isNew && !agentHandoffSettingDraft.agentId
  )
  const originalAgentHandoffSetting = agentHandoffSettingDraft
    ? agentHandoffSettings.find(
        (setting) => setting.agentId === agentHandoffSettingDraft.agentId
      )
    : undefined
  const isAgentHandoffSettingDraftDirty = Boolean(
    agentHandoffSettingDraft &&
    originalAgentHandoffSetting &&
    (agentHandoffSettingDraft.mode !== originalAgentHandoffSetting.mode ||
      !haveSameIds(
        agentHandoffSettingDraft.destinationIds,
        originalAgentHandoffSetting.destinationIds
      ))
  )

  const saveRule = async () => {
    if (!ruleDraft || !canEdit) return
    const name =
      ruleDraft.eventType === "form_request.submitted"
        ? internalFormRequestRuleName(
            (ruleDraft.agentId || "").trim(),
            ruleDraft.schemaId.trim()
          )
        : ruleDraft.name.trim()
    if (
      !name ||
      conditionalRuleRequiresSchema ||
      hasUnconditionalRuleConflict ||
      enabledRuleHasNoSelectedDestination ||
      enabledRuleHasNoReadyDestination
    ) {
      return
    }
    const conditions = ruleDraft.schemaId.trim()
      ? {
          agent_id: (ruleDraft.agentId || "").trim(),
          schema_id: ruleDraft.schemaId.trim(),
        }
      : null
    setIsSaving(true)
    try {
      if (ruleDraft.id) {
        await updateRule({
          urlBuilderParams: { ruleId: ruleDraft.id },
          jsonBody: {
            name,
            conditions,
            is_enabled: ruleDraft.isEnabled,
            destination_ids:
              isFormIndividualRuleDraft && !ruleDraft.isEnabled
                ? []
                : ruleDraft.destinationIds,
          },
        })
      } else {
        await createRule({
          jsonBody: {
            name,
            event_type: ruleDraft.eventType,
            conditions,
            is_enabled: ruleDraft.isEnabled,
            destination_ids:
              isFormIndividualRuleDraft && !ruleDraft.isEnabled
                ? []
                : ruleDraft.destinationIds,
          },
        })
      }
      setRuleDraft(null)
      successToast({ title: t("workspace.notifications.rules.saved") })
      await refresh()
    } catch (error) {
      const normalizedError = normalizeApiError(error)
      showError(
        error,
        t("workspace.notifications.errors.saveRule"),
        normalizedError.status === 409
          ? t("workspace.notifications.errors.ruleConflict")
          : t("workspace.notifications.warnings.ruleValidation")
      )
    } finally {
      setIsSaving(false)
    }
  }

  const removeRule = (
    event: NotificationEventSchema,
    rule: NotificationRuleSchema
  ) => {
    if (!canEdit) return
    const isFormIndividualSetting =
      event.event_type === "form_request.submitted" && rule.conditions !== null
    setConfirmationRequest({
      title: t("workspace.notifications.rules.deleteNamed", {
        name: ruleCollectionDisplayName(event, rule),
      }),
      description: t(
        isFormIndividualSetting
          ? rule.is_enabled
            ? "workspace.notifications.rules.deleteFormSettingConfirm"
            : "workspace.notifications.rules.deleteDisabledFormSettingConfirm"
          : "workspace.notifications.rules.deleteConfirm"
      ),
      confirmText: t(
        isFormIndividualSetting
          ? "workspace.notifications.rules.deleteFormSettingConfirmAction"
          : "workspace.notifications.rules.deleteConfirmAction"
      ),
      destructive: true,
      onConfirm: () => {
        void deleteRuleConfirmed(rule)
      },
    })
  }

  const deleteRuleConfirmed = async (rule: NotificationRuleSchema) => {
    try {
      await deleteRule({ urlBuilderParams: { ruleId: rule.id } })
      successToast({ title: t("workspace.notifications.rules.deleted") })
      await refresh()
    } catch (error) {
      showError(error, t("workspace.notifications.errors.deleteRule"))
    }
  }

  const updateRuleEnabled = async (
    rule: NotificationRuleSchema,
    isFormIndividualSetting = false
  ) => {
    if (!canEdit || updatingRuleIds.has(rule.id)) return
    const isEnabled = !rule.is_enabled
    setUpdatingRuleIds((current) => new Set(current).add(rule.id))
    try {
      await updateRule({
        urlBuilderParams: { ruleId: rule.id },
        jsonBody: { is_enabled: isEnabled },
      })
      await refresh()
      successToast({
        title: t(
          isFormIndividualSetting
            ? isEnabled
              ? "workspace.notifications.rules.customEnabledSuccess"
              : "workspace.notifications.rules.notificationsDisabledSuccess"
            : isEnabled
              ? "workspace.notifications.rules.enabledSuccess"
              : "workspace.notifications.rules.disabledSuccess"
        ),
      })
    } catch (error) {
      const normalizedError = normalizeApiError(error)
      showError(
        error,
        t("workspace.notifications.errors.saveRule"),
        normalizedError.status === 409
          ? t("workspace.notifications.errors.ruleConflict")
          : t("workspace.notifications.warnings.ruleValidation")
      )
    } finally {
      setUpdatingRuleIds((current) => {
        const next = new Set(current)
        next.delete(rule.id)
        return next
      })
    }
  }

  const saveAgentHandoffSetting = async () => {
    if (
      !agentHandoffSettingDraft ||
      !canEdit ||
      agentHandoffRequiresAgent ||
      agentCustomHasNoReadyDestination
    ) {
      return
    }
    setIsSaving(true)
    try {
      await updateAgentHandoffSetting({
        urlBuilderParams: { agentId: agentHandoffSettingDraft.agentId },
        jsonBody: {
          mode: agentHandoffSettingDraft.mode,
          destination_ids:
            agentHandoffSettingDraft.mode === "custom"
              ? agentHandoffSettingDraft.destinationIds
              : [],
        },
      })
      setAgentHandoffSettingDraft(null)
      successToast({
        title: t("workspace.notifications.agentSettings.saved"),
      })
      await refresh()
    } catch (error) {
      showError(
        error,
        t("workspace.notifications.errors.saveAgentSetting"),
        t("workspace.notifications.warnings.agentSettingValidation")
      )
    } finally {
      setIsSaving(false)
    }
  }

  const updateAgentHandoffSettingEnabled = async (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => {
    if (!canEdit || updatingAgentSettingIds.has(setting.agentId)) return
    const isEnabled = setting.mode !== "custom"
    setUpdatingAgentSettingIds((current) =>
      new Set(current).add(setting.agentId)
    )
    try {
      await updateAgentHandoffSetting({
        urlBuilderParams: { agentId: setting.agentId },
        jsonBody: {
          mode: isEnabled ? "custom" : "disabled",
          destination_ids: isEnabled ? setting.destinationIds : [],
        },
      })
      await refresh()
      successToast({
        title: t(
          isEnabled
            ? "workspace.notifications.agentSettings.enabledSuccess"
            : "workspace.notifications.agentSettings.disabledSuccess"
        ),
      })
    } catch (error) {
      showError(
        error,
        t("workspace.notifications.errors.saveAgentSetting"),
        t("workspace.notifications.warnings.agentSettingValidation")
      )
    } finally {
      setUpdatingAgentSettingIds((current) => {
        const next = new Set(current)
        next.delete(setting.agentId)
        return next
      })
    }
  }

  const removeAgentHandoffSetting = (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => {
    if (!canEdit) return
    setConfirmationRequest({
      title: t("workspace.notifications.agentSettings.deleteNamed", {
        agent: setting.agentName,
      }),
      description: t(
        setting.mode === "disabled"
          ? "workspace.notifications.agentSettings.deleteDisabledConfirm"
          : "workspace.notifications.agentSettings.deleteConfirm"
      ),
      confirmText: t(
        "workspace.notifications.agentSettings.deleteConfirmAction"
      ),
      destructive: true,
      onConfirm: () => {
        void deleteAgentHandoffSettingConfirmed(setting)
      },
    })
  }

  const deleteAgentHandoffSettingConfirmed = async (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => {
    try {
      await deleteAgentHandoffSetting({
        urlBuilderParams: { agentId: setting.agentId },
      })
      setAgentHandoffSettingDraft(null)
      successToast({
        title: t("workspace.notifications.agentSettings.deleted"),
      })
      await refresh()
    } catch (error) {
      showError(error, t("workspace.notifications.errors.deleteAgentSetting"))
    }
  }

  const ruleConditionLabel = (
    event: NotificationEventSchema,
    rule: NotificationRuleSchema
  ) => {
    const scope = formRuleScope(rule.conditions)
    if (scope) {
      const option = formSchemaOptionMap.get(
        formScopeKey(scope.agentId, scope.schemaId)
      )
      return t("workspace.notifications.rules.schemaCondition", {
        formName: option
          ? formSchemaOptionName(option)
          : formScopeFallbackName(scope.agentId, scope.schemaId),
      })
    }
    return event.event_type === "agent_handoff.triggered"
      ? t("workspace.notifications.sections.workspaceRule.title")
      : t("workspace.notifications.rules.formRequestsUnconditional")
  }

  const ruleCollectionDisplayName = (
    event: NotificationEventSchema,
    rule: NotificationRuleSchema
  ) => {
    if (event.event_type === "form_request.submitted") {
      const scope = formRuleScope(rule.conditions)
      if (!scope) {
        return t("workspace.notifications.rules.formRequestsUnconditional")
      }
      const option = formSchemaOptionMap.get(
        formScopeKey(scope.agentId, scope.schemaId)
      )
      return option
        ? formSchemaOptionName(option)
        : formScopeFallbackName(scope.agentId, scope.schemaId)
    }
    return event.event_type === "agent_handoff.triggered"
      ? t("workspace.notifications.rules.allAgents")
      : ruleDisplayName(rule)
  }

  const renderRuleStatus = (
    rule: NotificationRuleSchema,
    isFormIndividualSetting = false
  ) => {
    const isUpdating = updatingRuleIds.has(rule.id)
    const cannotEnable =
      !rule.is_enabled &&
      !rule.destination_ids.some(
        (destinationId) => destinationMap.get(destinationId)?.isReady
      )
    const actionLabel = t(
      isFormIndividualSetting
        ? rule.is_enabled
          ? "workspace.notifications.rules.disableCustom"
          : "workspace.notifications.rules.enableCustom"
        : rule.is_enabled
          ? "workspace.notifications.rules.disable"
          : "workspace.notifications.rules.enable"
    )
    const statusLabel = t(
      isFormIndividualSetting
        ? rule.is_enabled
          ? "workspace.notifications.rules.behaviors.custom"
          : "workspace.notifications.rules.behaviors.disabled"
        : rule.is_enabled
          ? "workspace.notifications.enabled"
          : "workspace.notifications.disabled"
    )

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-2">
              <Switch
                checked={rule.is_enabled}
                disabled={!canEdit || isUpdating || cannotEnable}
                onCheckedChange={() =>
                  void updateRuleEnabled(rule, isFormIndividualSetting)
                }
                aria-label={actionLabel}
              />
              <Badge
                variant="outline"
                className={cn(
                  "font-medium",
                  rule.is_enabled
                    ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300"
                    : "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
                )}
              >
                {statusLabel}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {!canEdit
              ? t("workspace.notifications.viewOnly")
              : isUpdating
                ? t("workspace.notifications.rules.updatingStatus")
                : cannotEnable
                  ? t("workspace.notifications.rules.noReadyDestinationWarning")
                  : isFormIndividualSetting
                    ? t(
                        rule.is_enabled
                          ? "workspace.notifications.rules.behaviorHelp.custom"
                          : "workspace.notifications.rules.behaviorHelp.disabled"
                      )
                    : actionLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const ruleDestinations = (destinationIds: string[]) =>
    destinationIds.flatMap((id) => {
      const destination = destinationMap.get(id)
      return destination ? [destination] : []
    })

  const renderDestinationList = (destinationIds: string[]) => {
    const destinations = ruleDestinations(destinationIds)
    return (
      <div>
        {destinations.length ? (
          <div className="space-y-1.5">
            {destinations.map((destination) => {
              const DestinationIcon =
                destination.destinationType === "email" ? Mail : Webhook
              return (
                <div
                  key={destination.id}
                  className="flex min-w-0 items-start gap-2"
                  title={destination.name}
                >
                  <DestinationIcon
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  />
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    <span className="min-w-0 break-words text-sm leading-snug">
                      {destination.name}
                    </span>
                    {!destination.isReady ? (
                      <DestinationStatusBadge
                        destination={destination}
                        readyLabel={t("workspace.notifications.ready")}
                        pendingLabel={t("workspace.notifications.pending")}
                        disabledLabel={t("workspace.notifications.disabled")}
                        verificationPendingLabel={t(
                          "workspace.notifications.destinations.verificationPending"
                        )}
                        verificationExpiredLabel={t(
                          "workspace.notifications.destinations.verificationExpired"
                        )}
                      />
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div>{t("workspace.notifications.rules.none")}</div>
        )}
      </div>
    )
  }

  const renderRuleDestinations = (
    rule: NotificationRuleSchema,
    isFormIndividualSetting = false
  ) => (
    <div
      className={cn(
        "space-y-1",
        isFormIndividualSetting && !rule.is_enabled && "text-muted-foreground"
      )}
    >
      {renderDestinationList(rule.destination_ids)}
    </div>
  )

  const renderDestinationUsage = (destination: NotificationDestination) => {
    if (!destination.usedBy.length) {
      return (
        <span className="text-muted-foreground">
          {t("workspace.notifications.destinations.unmanaged")}
        </span>
      )
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Badge variant="secondary" asChild>
            <button
              type="button"
              className="cursor-pointer font-normal"
              onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
              {t("workspace.notifications.destinations.usageCount", {
                count: destination.usedBy.length,
              })}
              <ChevronDown />
            </button>
          </Badge>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-72 p-3"
          onClick={(clickEvent) => clickEvent.stopPropagation()}
        >
          <p className="text-xs font-medium">
            {t("workspace.notifications.destinations.inUse")}
          </p>
          <ul className="mt-2 max-h-72 list-disc space-y-1.5 overflow-y-auto pl-4 pr-1 text-sm">
            {destination.usedBy.map((rule) => (
              <li key={rule.id} className="break-words">
                {ruleReferenceDisplayName(rule)}
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    )
  }

  const renderRuleEditAffordance = (
    event: NotificationEventSchema,
    rule: NotificationRuleSchema,
    alwaysVisible = false
  ) =>
    canEdit ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 shrink-0 text-muted-foreground",
                !alwaysVisible &&
                  "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
              )}
              aria-label={t("workspace.notifications.rules.editNamed", {
                name: ruleCollectionDisplayName(event, rule),
              })}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation()
                openRuleEditor(event, rule)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("workspace.notifications.rules.edit")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : null

  const renderRuleDeleteAction = (
    event: NotificationEventSchema,
    rule: NotificationRuleSchema
  ) => {
    const isDisabled = !canEdit
    const tooltip = !canEdit
      ? t("workspace.notifications.viewOnly")
      : t("workspace.notifications.rules.delete")

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex"
              onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                disabled={isDisabled}
                aria-label={t("workspace.notifications.rules.deleteNamed", {
                  name: ruleCollectionDisplayName(event, rule),
                })}
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation()
                  void removeRule(event, rule)
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const openDestinationEditor = (destination: NotificationDestination) => {
    setDestinationDraft({
      id: destination.id,
      name: destination.name,
      destinationType: destination.destinationType,
      originalDestinationType: destination.destinationType,
      endpoint: "",
      usedBy: destination.usedBy,
    })
  }

  const renderDestinationEditAffordance = (
    destination: NotificationDestination,
    alwaysVisible = false
  ) =>
    canEdit ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 shrink-0 text-muted-foreground",
                !alwaysVisible &&
                  "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
              )}
              aria-label={t("workspace.notifications.destinations.editNamed", {
                name: destination.name,
              })}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation()
                openDestinationEditor(destination)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("workspace.notifications.destinations.edit")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : null

  const renderDestinationStatus = (destination: NotificationDestination) => {
    const isUpdating = updatingDestinationIds.has(destination.id)
    const actionLabel = destination.isEnabled
      ? t("workspace.notifications.destinations.disable")
      : t("workspace.notifications.destinations.enable")

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="inline-flex items-center gap-2"
              onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
              <Switch
                checked={destination.isEnabled}
                disabled={!canEdit || isUpdating}
                onCheckedChange={() =>
                  void updateDestinationEnabled(destination)
                }
                aria-label={actionLabel}
              />
              <DestinationStatusBadge
                destination={destination}
                readyLabel={t("workspace.notifications.ready")}
                pendingLabel={t("workspace.notifications.pending")}
                disabledLabel={t("workspace.notifications.disabled")}
                verificationPendingLabel={t(
                  "workspace.notifications.destinations.verificationPending"
                )}
                verificationExpiredLabel={t(
                  "workspace.notifications.destinations.verificationExpired"
                )}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {!canEdit
              ? t("workspace.notifications.viewOnly")
              : isUpdating
                ? t("workspace.notifications.destinations.updatingStatus")
                : actionLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const renderDestinationPrimaryAction = (
    destination: NotificationDestination
  ) => {
    const emailNeedsVerification =
      destination.destinationType === "email" &&
      destination.verificationStatus !== "verified"
    const action = emailNeedsVerification ? "verify" : "test"
    const resendSeconds = destination.verificationResendAvailableAt
      ? Math.max(
          0,
          Math.ceil(
            (destination.verificationResendAvailableAt - verificationClock) /
              1000
          )
        )
      : 0
    const isRunning = runningDestinationActionIds.has(destination.id)

    if (emailNeedsVerification && resendSeconds > 0) {
      return (
        <span className="inline-flex h-8 items-center text-xs text-muted-foreground">
          {t(
            "workspace.notifications.destinations.verificationResendCountdown",
            { time: formatCountdown(resendSeconds) }
          )}
        </span>
      )
    }

    const label = t(
      emailNeedsVerification
        ? isRunning
          ? "workspace.notifications.destinations.sendingVerification"
          : "workspace.notifications.destinations.verificationResend"
        : "workspace.notifications.destinations.test"
    )
    const ActionIcon = emailNeedsVerification ? Mail : Send

    return (
      <PermissionButton
        permission={Permission.WsUpdateResources}
        workspace={workspace}
        disabledTooltip={t("workspace.notifications.viewOnly")}
        disabled={isRunning}
        variant="ghost"
        size="sm"
        onClick={(clickEvent) => {
          clickEvent.stopPropagation()
          void runDestinationAction(destination, action)
        }}
      >
        <ActionIcon className="mr-1.5 h-3.5 w-3.5" />
        {label}
      </PermissionButton>
    )
  }

  const renderDestinationDeleteAction = (
    destination: NotificationDestination
  ) => {
    const isDisabled = !canEdit || destination.usedBy.length > 0
    const tooltip = !canEdit
      ? t("workspace.notifications.viewOnly")
      : destination.usedBy.length > 0
        ? t("workspace.notifications.destinations.deleteUnavailable", {
            rules: ruleReferenceList(destination.usedBy),
          })
        : t("workspace.notifications.destinations.delete")

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex"
              onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                disabled={isDisabled}
                aria-label={t(
                  "workspace.notifications.destinations.deleteNamed",
                  {
                    name: destination.name,
                  }
                )}
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation()
                  void removeDestination(destination)
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const renderDestinationActions = (destination: NotificationDestination) => {
    return (
      <div
        className="flex items-center justify-end gap-1"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {renderDestinationPrimaryAction(destination)}
        {renderDestinationDeleteAction(destination)}
      </div>
    )
  }

  const renderRuleCollection = (
    event: NotificationEventSchema,
    rules: NotificationRuleSchema[],
    emptyLabel: string,
    isIndividualSetting = false
  ) => {
    const isFormRequest = event.event_type === "form_request.submitted"
    const isAgentHandoff = event.event_type === "agent_handoff.triggered"
    const isFormIndividualSetting = isFormRequest && isIndividualSetting
    const usesScopeColumn = isFormRequest || isAgentHandoff
    return (
      <>
        <div className="space-y-2 lg:hidden">
          {rules.length === 0 ? (
            <div className="rounded-md border px-4 py-8 text-center text-sm text-muted-foreground">
              {emptyLabel}
            </div>
          ) : (
            rules.map((rule) => (
              <article
                key={rule.id}
                className={cn(
                  "group space-y-3 rounded-md border p-4 transition-colors",
                  canEdit && "cursor-pointer hover:bg-muted/50"
                )}
                onClick={
                  canEdit ? () => openRuleEditor(event, rule) : undefined
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-start gap-1">
                      <p className="min-w-0 break-words font-medium">
                        {ruleCollectionDisplayName(event, rule)}
                      </p>
                      {renderRuleEditAffordance(event, rule, true)}
                    </div>
                  </div>
                  {renderRuleDeleteAction(event, rule)}
                </div>
                <dl className="grid gap-2 text-sm">
                  {!usesScopeColumn ? (
                    <div>
                      <dt className="text-xs text-muted-foreground">
                        {t("workspace.notifications.rules.condition")}
                      </dt>
                      <dd className="break-words">
                        {ruleConditionLabel(event, rule)}
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-xs text-muted-foreground">
                      {t("workspace.notifications.rules.selectedDestinations")}
                    </dt>
                    <dd className="break-words">
                      {renderRuleDestinations(rule, isFormIndividualSetting)}
                    </dd>
                  </div>
                </dl>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      isFormIndividualSetting
                        ? "workspace.notifications.agentSettings.mode"
                        : "workspace.notifications.rules.status"
                    )}
                  </p>
                  <div
                    className="mt-1 w-fit"
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    {renderRuleStatus(rule, isFormIndividualSetting)}
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
        <div className="hidden overflow-hidden rounded-md border lg:block">
          <Table className="table-fixed">
            {usesScopeColumn ? (
              <colgroup>
                <col className="w-[32%]" />
                <col />
                <col className="w-40" />
                <col className="w-14" />
              </colgroup>
            ) : (
              <colgroup>
                <col className="w-[25%]" />
                <col className="w-[20%]" />
                <col />
                <col className="w-40" />
                <col className="w-14" />
              </colgroup>
            )}
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t(
                    isFormRequest
                      ? "workspace.notifications.rules.schemaId"
                      : isAgentHandoff
                        ? "workspace.notifications.rules.applicableAgents"
                        : "workspace.notifications.rules.name"
                  )}
                </TableHead>
                {!usesScopeColumn ? (
                  <TableHead>
                    {t("workspace.notifications.rules.condition")}
                  </TableHead>
                ) : null}
                <TableHead>
                  {t("workspace.notifications.rules.selectedDestinations")}
                </TableHead>
                <TableHead>
                  {t(
                    isFormIndividualSetting
                      ? "workspace.notifications.agentSettings.mode"
                      : "workspace.notifications.rules.status"
                  )}
                </TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">
                    {t("workspace.notifications.actions")}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={usesScopeColumn ? 4 : 5}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {emptyLabel}
                  </TableCell>
                </TableRow>
              ) : (
                rules.map((rule) => (
                  <TableRow
                    key={rule.id}
                    className={cn("group", canEdit && "cursor-pointer")}
                    onClick={
                      canEdit ? () => openRuleEditor(event, rule) : undefined
                    }
                  >
                    <TableCell>
                      <div className="flex items-start gap-1">
                        <div className="min-w-0">
                          <div className="break-words font-medium">
                            {ruleCollectionDisplayName(event, rule)}
                          </div>
                        </div>
                        {renderRuleEditAffordance(event, rule)}
                      </div>
                    </TableCell>
                    {!usesScopeColumn ? (
                      <TableCell className="whitespace-normal break-words">
                        {ruleConditionLabel(event, rule)}
                      </TableCell>
                    ) : null}
                    <TableCell className="whitespace-normal break-words">
                      {renderRuleDestinations(rule, isFormIndividualSetting)}
                    </TableCell>
                    <TableCell
                      onClick={(clickEvent) => clickEvent.stopPropagation()}
                    >
                      {renderRuleStatus(rule, isFormIndividualSetting)}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(clickEvent) => clickEvent.stopPropagation()}
                    >
                      {renderRuleDeleteAction(event, rule)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </>
    )
  }

  const renderAgentStatus = (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => {
    const isEnabled = setting.mode === "custom"
    const isUpdating = updatingAgentSettingIds.has(setting.agentId)
    const cannotEnable =
      !isEnabled &&
      !setting.destinationIds.some(
        (destinationId) => destinationMap.get(destinationId)?.isReady
      )
    const actionLabel = t(
      isEnabled
        ? "workspace.notifications.agentSettings.disable"
        : "workspace.notifications.agentSettings.enable"
    )

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-2">
              <Switch
                checked={isEnabled}
                disabled={!canEdit || isUpdating || cannotEnable}
                onCheckedChange={() =>
                  void updateAgentHandoffSettingEnabled(setting)
                }
                aria-label={actionLabel}
              />
              <Badge
                variant="outline"
                className={cn(
                  "font-medium",
                  isEnabled
                    ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300"
                    : "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
                )}
              >
                {t(
                  isEnabled
                    ? "workspace.notifications.agentSettings.behaviors.custom"
                    : "workspace.notifications.agentSettings.behaviors.disabled"
                )}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {!canEdit
              ? t("workspace.notifications.viewOnly")
              : isUpdating
                ? t("workspace.notifications.agentSettings.updatingStatus")
                : cannotEnable
                  ? t(
                      "workspace.notifications.agentSettings.noReadyDestinationWarning"
                    )
                  : t(
                      isEnabled
                        ? "workspace.notifications.agentSettings.behaviorHelp.custom"
                        : "workspace.notifications.agentSettings.behaviorHelp.disabled"
                    )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const renderAgentDestinations = (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => (
    <div
      className={cn(
        "space-y-1",
        setting.mode === "disabled" && "text-muted-foreground"
      )}
    >
      {renderDestinationList(setting.destinationIds)}
    </div>
  )

  const openAgentHandoffSettingEditor = (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => {
    if (!canEdit) return
    setAgentHandoffSettingDraft(agentHandoffSettingToDraft(setting))
  }

  const renderAgentEditAffordance = (
    setting: WorkspaceAgentHandoffNotificationSetting,
    alwaysVisible = false
  ) =>
    canEdit ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 shrink-0 text-muted-foreground",
                !alwaysVisible &&
                  "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
              )}
              aria-label={t("workspace.notifications.agentSettings.editNamed", {
                agent: setting.agentName,
              })}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation()
                openAgentHandoffSettingEditor(setting)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("workspace.notifications.agentSettings.edit")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : null

  const renderAgentDeleteAction = (
    setting: WorkspaceAgentHandoffNotificationSetting
  ) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex"
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              disabled={!canEdit}
              aria-label={t(
                "workspace.notifications.agentSettings.deleteNamed",
                {
                  agent: setting.agentName,
                }
              )}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation()
                removeAgentHandoffSetting(setting)
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {canEdit
            ? t("workspace.notifications.agentSettings.delete")
            : t("workspace.notifications.viewOnly")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )

  const agentHandoffOverrides = agentHandoffSettings.filter(
    (setting) => setting.mode !== "workspace_default"
  )
  const agentsUsingWorkspaceDefault = agentHandoffSettings.filter(
    (setting) => setting.mode === "workspace_default"
  )
  const selectAgentForHandoffSetting = (agentId: string) => {
    const setting = agentsUsingWorkspaceDefault.find(
      (candidate) => candidate.agentId === agentId
    )
    if (!setting) return
    setAgentHandoffSettingDraft((current) =>
      current
        ? {
            ...agentHandoffSettingToDraft(setting),
            mode: current.mode,
            destinationIds: current.destinationIds,
          }
        : null
    )
  }
  const agentHandoffEmptyLabel =
    agentHandoffSettings.length === 0
      ? t("workspace.notifications.agentSettings.noAgents")
      : t("workspace.notifications.agentSettings.empty")

  const renderAgentHandoffSettings = () => (
    <>
      <div className="space-y-2 lg:hidden">
        {agentHandoffOverrides.length === 0 ? (
          <div className="rounded-md border px-4 py-8 text-center text-sm text-muted-foreground">
            {agentHandoffEmptyLabel}
          </div>
        ) : (
          agentHandoffOverrides.map((setting) => (
            <article
              key={setting.agentId}
              className={cn(
                "group space-y-3 rounded-md border p-4 transition-colors",
                canEdit && "cursor-pointer hover:bg-muted/50",
                highlightedAgentId === setting.agentId &&
                  "bg-muted/60 ring-1 ring-border"
              )}
              onClick={() => openAgentHandoffSettingEditor(setting)}
            >
              <div className="flex items-start gap-2">
                <Bot className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium">{setting.agentName}</p>
                </div>
                <div className="flex items-center gap-1">
                  {renderAgentEditAffordance(setting, true)}
                  {renderAgentDeleteAction(setting)}
                </div>
              </div>
              <dl className="grid gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">
                    {t("workspace.notifications.rules.selectedDestinations")}
                  </dt>
                  <dd className="mt-1">{renderAgentDestinations(setting)}</dd>
                </div>
              </dl>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("workspace.notifications.agentSettings.mode")}
                </p>
                <div
                  className="mt-1 w-fit"
                  onClick={(clickEvent) => clickEvent.stopPropagation()}
                >
                  {renderAgentStatus(setting)}
                </div>
              </div>
            </article>
          ))
        )}
      </div>
      <div className="hidden overflow-hidden rounded-md border lg:block">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-[32%]" />
            <col />
            <col className="w-40" />
            <col className="w-14" />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t("workspace.notifications.agentSettings.agent")}
              </TableHead>
              <TableHead>
                {t("workspace.notifications.rules.selectedDestinations")}
              </TableHead>
              <TableHead>
                {t("workspace.notifications.agentSettings.mode")}
              </TableHead>
              <TableHead className="text-right">
                <span className="sr-only">
                  {t("workspace.notifications.actions")}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agentHandoffOverrides.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="h-24 text-center text-muted-foreground"
                >
                  {agentHandoffEmptyLabel}
                </TableCell>
              </TableRow>
            ) : (
              agentHandoffOverrides.map((setting) => (
                <TableRow
                  key={setting.agentId}
                  className={cn(
                    "group",
                    canEdit && "cursor-pointer",
                    highlightedAgentId === setting.agentId && "bg-muted/60"
                  )}
                  onClick={() => openAgentHandoffSettingEditor(setting)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4 text-muted-foreground" />
                      <span className="break-words font-medium">
                        {setting.agentName}
                      </span>
                      {renderAgentEditAffordance(setting)}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {renderAgentDestinations(setting)}
                  </TableCell>
                  <TableCell
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    {renderAgentStatus(setting)}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    {renderAgentDeleteAction(setting)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )

  const selectedEvent =
    events.find((event) => event.event_type === focusedEventType) || events[0]
  const selectedWorkspaceRules =
    selectedEvent?.rules.filter((rule) => rule.conditions === null) || []
  const selectedSpecificRules =
    selectedEvent?.rules.filter((rule) => rule.conditions !== null) || []

  const selectEvent = (eventType: string) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set("event", eventType)
    nextSearchParams.delete("agent")
    nextSearchParams.delete("rule")
    setSearchParams(nextSearchParams, { replace: true })
  }

  const eventSpecificSettingCount = (event: NotificationEventSchema) =>
    event.event_type === "agent_handoff.triggered"
      ? agentHandoffOverrides.length
      : event.rules.filter((rule) => rule.conditions !== null).length

  const eventWorkspaceRuleStatus = (event: NotificationEventSchema) => {
    const workspaceRules = event.rules.filter(
      (rule) => rule.conditions === null
    )
    if (workspaceRules.some((rule) => rule.is_enabled)) {
      return t("workspace.notifications.enabled")
    }
    if (workspaceRules.length > 0) {
      return t("workspace.notifications.disabled")
    }
    return t("workspace.notifications.eventNavigation.notConfigured")
  }

  if (isLoading) return <LoadingPage />

  return (
    <div className="space-y-6">
      <div className="max-w-3xl">
        <h2 className="text-xl font-semibold">
          {t("workspace.notifications.title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("workspace.notifications.description")}
        </p>
        {canEdit ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("workspace.notifications.rowEditHint")}
          </p>
        ) : null}
        {!canEdit ? (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            {t("workspace.notifications.viewOnly")}
          </p>
        ) : null}
      </div>

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules">
            {t("workspace.notifications.tabs.rules")}
          </TabsTrigger>
          <TabsTrigger value="destinations">
            {t("workspace.notifications.tabs.destinations")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-5">
          {selectedEvent ? (
            <div className="grid min-w-0 gap-6 min-[1200px]:grid-cols-[16rem_minmax(0,1fr)]">
              <aside className="hidden min-[1200px]:block">
                <nav
                  aria-label={t(
                    "workspace.notifications.eventNavigation.title"
                  )}
                  className="sticky top-4 space-y-1"
                >
                  <p className="mb-2 px-3 text-xs font-medium text-muted-foreground">
                    {t("workspace.notifications.eventNavigation.title")}
                  </p>
                  {events.map((event) => {
                    const isSelected =
                      event.event_type === selectedEvent.event_type
                    return (
                      <button
                        key={event.event_type}
                        type="button"
                        aria-current={isSelected ? "page" : undefined}
                        aria-controls={`notification-event-${event.event_type}`}
                        className={cn(
                          "w-full rounded-md px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                          isSelected
                            ? "bg-muted text-foreground"
                            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        )}
                        onClick={() => selectEvent(event.event_type)}
                      >
                        <span className="block text-sm font-medium">
                          {t(
                            `workspace.notifications.events.${event.event_type}.name`
                          )}
                        </span>
                        <span className="mt-1 block text-xs">
                          {t(
                            "workspace.notifications.eventNavigation.workspaceRuleStatus",
                            {
                              status: eventWorkspaceRuleStatus(event),
                            }
                          )}
                        </span>
                        <span className="mt-0.5 block text-xs">
                          {t(
                            "workspace.notifications.eventNavigation.specificCount",
                            {
                              count: eventSpecificSettingCount(event),
                            }
                          )}
                        </span>
                      </button>
                    )
                  })}
                </nav>
              </aside>

              <div className="min-w-0 space-y-5">
                <div className="space-y-2 min-[1200px]:hidden">
                  <Label htmlFor="notification-event-select">
                    {t("workspace.notifications.eventNavigation.selectLabel")}
                  </Label>
                  <Select
                    value={selectedEvent.event_type}
                    onValueChange={selectEvent}
                  >
                    <SelectTrigger id="notification-event-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {events.map((event) => (
                        <SelectItem
                          key={event.event_type}
                          value={event.event_type}
                        >
                          {t(
                            `workspace.notifications.events.${event.event_type}.name`
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <section
                  id={`notification-event-${selectedEvent.event_type}`}
                  aria-labelledby={`notification-event-${selectedEvent.event_type}-title`}
                  className="min-w-0 space-y-6"
                >
                  <div className="border-b pb-4">
                    <h3
                      id={`notification-event-${selectedEvent.event_type}-title`}
                      className="font-semibold"
                    >
                      {t(
                        `workspace.notifications.events.${selectedEvent.event_type}.name`
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        `workspace.notifications.events.${selectedEvent.event_type}.description`
                      )}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold">
                          {t(
                            "workspace.notifications.sections.workspaceRule.title"
                          )}
                        </h4>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {t(
                            `workspace.notifications.sections.workspaceRule.${
                              selectedEvent.event_type ===
                              "agent_handoff.triggered"
                                ? "handoffDescription"
                                : "formDescription"
                            }`
                          )}
                        </p>
                      </div>
                      {selectedWorkspaceRules.length === 0 ? (
                        <PermissionButton
                          permission={Permission.WsUpdateResources}
                          workspace={workspace}
                          disabledTooltip={t(
                            "workspace.notifications.viewOnly"
                          )}
                          size="sm"
                          onClick={() =>
                            openRuleEditor(selectedEvent, undefined, false)
                          }
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          {t(
                            "workspace.notifications.rules.createWorkspaceRule"
                          )}
                        </PermissionButton>
                      ) : null}
                    </div>
                    {renderRuleCollection(
                      selectedEvent,
                      selectedWorkspaceRules,
                      t("workspace.notifications.sections.workspaceRule.empty")
                    )}
                  </div>

                  {(selectedEvent.event_type === "agent_handoff.triggered" ||
                    selectedEvent.event_type === "form_request.submitted") && (
                    <div className="space-y-3 border-t pt-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold">
                            {t(
                              "workspace.notifications.sections.specificSettings.title"
                            )}
                          </h4>
                          <p className="mt-0.5 text-sm text-muted-foreground">
                            {t(
                              `workspace.notifications.sections.specificSettings.${
                                selectedEvent.event_type ===
                                "agent_handoff.triggered"
                                  ? "handoffDescription"
                                  : "formDescription"
                              }`
                            )}
                          </p>
                        </div>
                        {selectedEvent.event_type ===
                        "form_request.submitted" ? (
                          <PermissionButton
                            permission={Permission.WsUpdateResources}
                            workspace={workspace}
                            disabledTooltip={
                              canEdit
                                ? t(
                                    formSchemaOptions.length === 0
                                      ? "workspace.notifications.rules.noForms"
                                      : "workspace.notifications.rules.allFormsConfigured"
                                  )
                                : t("workspace.notifications.viewOnly")
                            }
                            disabled={
                              availableNewFormSchemaOptions.length === 0
                            }
                            size="sm"
                            onClick={() =>
                              openRuleEditor(selectedEvent, undefined, true)
                            }
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {t(
                              "workspace.notifications.rules.createConditional"
                            )}
                          </PermissionButton>
                        ) : selectedEvent.event_type ===
                          "agent_handoff.triggered" ? (
                          <PermissionButton
                            permission={Permission.WsUpdateResources}
                            workspace={workspace}
                            disabledTooltip={t(
                              "workspace.notifications.viewOnly"
                            )}
                            disabled={agentsUsingWorkspaceDefault.length === 0}
                            size="sm"
                            onClick={() =>
                              setAgentHandoffSettingDraft(
                                emptyAgentHandoffSettingDraft(
                                  selectedEvent.allowed_destination_types
                                )
                              )
                            }
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {t("workspace.notifications.agentSettings.create")}
                          </PermissionButton>
                        ) : null}
                      </div>
                      {selectedEvent.event_type === "agent_handoff.triggered"
                        ? renderAgentHandoffSettings()
                        : renderRuleCollection(
                            selectedEvent,
                            selectedSpecificRules,
                            t(
                              "workspace.notifications.sections.specificSettings.empty"
                            ),
                            true
                          )}
                    </div>
                  )}
                </section>
              </div>
            </div>
          ) : (
            <div className="rounded-md border px-4 py-10 text-center text-sm text-muted-foreground">
              {t("workspace.notifications.eventNavigation.empty")}
            </div>
          )}
        </TabsContent>

        <TabsContent value="destinations" className="mt-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold">
                {t("workspace.notifications.destinations.title")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("workspace.notifications.destinations.description")}
              </p>
            </div>
            <PermissionButton
              permission={Permission.WsUpdateResources}
              workspace={workspace}
              disabledTooltip={t("workspace.notifications.viewOnly")}
              size="sm"
              onClick={() =>
                setDestinationDraft({ ...EMPTY_DESTINATION_DRAFT })
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("workspace.notifications.destinations.create")}
            </PermissionButton>
          </div>
          <div className="space-y-2 lg:hidden">
            {destinations.length === 0 ? (
              <div className="rounded-md border px-4 py-8 text-center text-sm text-muted-foreground">
                {t("workspace.notifications.destinations.empty")}
              </div>
            ) : (
              destinations.map((destination) => (
                <article
                  key={destination.id}
                  className={cn(
                    "group space-y-3 rounded-md border p-4 transition-colors",
                    canEdit && "cursor-pointer hover:bg-muted/50"
                  )}
                  onClick={
                    canEdit
                      ? () => openDestinationEditor(destination)
                      : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-1">
                      <div className="min-w-0">
                        <p className="break-words font-medium">
                          {destination.name}
                        </p>
                        <p className="break-all text-xs text-muted-foreground">
                          {destination.maskedEndpoint}
                        </p>
                      </div>
                      {renderDestinationEditAffordance(destination, true)}
                    </div>
                    {renderDestinationDeleteAction(destination)}
                  </div>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs text-muted-foreground">
                        {t("workspace.notifications.destinations.type")}
                      </dt>
                      <dd>
                        {t(
                          `workspace.notifications.destinationTypes.${destination.destinationType}`
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">
                        {t("workspace.notifications.destinations.status")}
                      </dt>
                      <dd className="mt-1">
                        {renderDestinationStatus(destination)}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs text-muted-foreground">
                        {t("workspace.notifications.destinations.usedBy")}
                      </dt>
                      <dd className="break-words">
                        {renderDestinationUsage(destination)}
                      </dd>
                    </div>
                  </dl>
                  <div className="flex justify-end">
                    {renderDestinationPrimaryAction(destination)}
                  </div>
                </article>
              ))
            )}
          </div>
          <div className="hidden overflow-hidden rounded-md border lg:block">
            <Table className="table-fixed">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[18%]" />
                <col />
                <col className="w-44" />
                <col className="w-48" />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("workspace.notifications.destinations.name")}
                  </TableHead>
                  <TableHead>
                    {t("workspace.notifications.destinations.type")}
                  </TableHead>
                  <TableHead>
                    {t("workspace.notifications.destinations.usedBy")}
                  </TableHead>
                  <TableHead>
                    {t("workspace.notifications.destinations.status")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("workspace.notifications.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {destinations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      {t("workspace.notifications.destinations.empty")}
                    </TableCell>
                  </TableRow>
                ) : (
                  destinations.map((destination) => (
                    <TableRow
                      key={destination.id}
                      className={cn("group", canEdit && "cursor-pointer")}
                      onClick={
                        canEdit
                          ? () => openDestinationEditor(destination)
                          : undefined
                      }
                    >
                      <TableCell className="whitespace-normal">
                        <div className="flex items-start gap-1">
                          <div className="min-w-0">
                            <div className="break-words font-medium">
                              {destination.name}
                            </div>
                            <div className="break-all text-xs text-muted-foreground">
                              {destination.maskedEndpoint}
                            </div>
                          </div>
                          {renderDestinationEditAffordance(destination)}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-normal break-words">
                        {t(
                          `workspace.notifications.destinationTypes.${destination.destinationType}`
                        )}
                      </TableCell>
                      <TableCell className="whitespace-normal break-words">
                        {renderDestinationUsage(destination)}
                      </TableCell>
                      <TableCell
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                      >
                        {renderDestinationStatus(destination)}
                      </TableCell>
                      <TableCell
                        className="text-right"
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                      >
                        {renderDestinationActions(destination)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog
        open={destinationDraft !== null}
        onOpenChange={(open) => !open && setDestinationDraft(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {destinationDraft?.id
                ? t("workspace.notifications.destinations.edit")
                : t("workspace.notifications.destinations.create")}
            </DialogTitle>
            <DialogDescription>
              {t("workspace.notifications.destinations.formDescription")}
            </DialogDescription>
          </DialogHeader>
          {destinationDraft ? (
            <>
              <DestinationFormFields
                name={destinationDraft.name}
                destinationType={destinationDraft.destinationType}
                endpoint={destinationDraft.endpoint}
                typeDisabled={
                  Boolean(destinationDraft.id) &&
                  destinationDraft.usedBy.length > 0
                }
                nameLabel={t("workspace.notifications.destinations.name")}
                typeLabel={t("workspace.notifications.destinations.type")}
                endpointLabel={
                  destinationDraft.destinationType === "email"
                    ? t("workspace.notifications.destinations.email")
                    : t("workspace.notifications.destinations.webhook")
                }
                endpointPlaceholder={
                  destinationDraft.id && !destinationTypeChanged
                    ? t("workspace.notifications.destinations.keepEndpoint")
                    : destinationEndpointPlaceholder(
                        destinationDraft.destinationType
                      )
                }
                slackIncomingLabel={t(
                  "workspace.notifications.destinationTypes.slack_webhook"
                )}
                slackWorkflowLabel={t(
                  "workspace.notifications.destinationTypes.slack_workflow_webhook"
                )}
                emailLabel={t("workspace.notifications.destinationTypes.email")}
                onNameChange={(name) =>
                  setDestinationDraft({ ...destinationDraft, name })
                }
                onDestinationTypeChange={(destinationType) =>
                  setDestinationDraft({
                    ...destinationDraft,
                    destinationType,
                    endpoint: "",
                  })
                }
                onEndpointChange={(endpoint) =>
                  setDestinationDraft({ ...destinationDraft, endpoint })
                }
              />
              {destinationDraft.usedBy.length > 1 ? (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                  {t(
                    "workspace.notifications.destinations.sharedImpactWarning",
                    {
                      rules: ruleReferenceList(destinationDraft.usedBy),
                    }
                  )}
                </p>
              ) : null}
            </>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDestinationDraft(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={isSaving || !destinationDraftIsValid}
              onClick={() => {
                void saveDestination()
              }}
            >
              {t("workspace.notifications.destinations.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={ruleDraft !== null}
        onOpenChange={(open) => !open && setRuleDraft(null)}
      >
        <DialogContent className="max-h-[calc(100dvh-2rem)] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isFormIndividualRuleDraft
                ? ruleDraft?.id
                  ? t("workspace.notifications.rules.editFormSettingTitle", {
                      form: ruleDraftFormName,
                    })
                  : t("workspace.notifications.rules.createFormSettingTitle")
                : ruleDraft?.id
                  ? t("workspace.notifications.rules.edit")
                  : t("workspace.notifications.rules.create")}
            </DialogTitle>
            <DialogDescription>
              {t(
                isFormIndividualRuleDraft
                  ? ruleDraft?.id
                    ? "workspace.notifications.rules.editFormSettingDescription"
                    : "workspace.notifications.rules.createFormSettingDescription"
                  : "workspace.notifications.rules.formDescription"
              )}
            </DialogDescription>
          </DialogHeader>
          {ruleDraft ? (
            <div className="min-h-0 min-w-0 space-y-4 overflow-y-auto overscroll-contain pr-1 [scrollbar-gutter:stable]">
              {!isSystemWorkspaceRuleDraft &&
              ruleDraft.eventType !== "form_request.submitted" ? (
                <div className="space-y-2">
                  <Label htmlFor="notification-rule-name">
                    {t("workspace.notifications.rules.name")}
                  </Label>
                  <Input
                    id="notification-rule-name"
                    value={ruleDraft.name}
                    onChange={(event) =>
                      setRuleDraft({ ...ruleDraft, name: event.target.value })
                    }
                  />
                </div>
              ) : null}
              {isFormIndividualRuleDraft && !ruleDraft.id ? (
                <div className="space-y-2">
                  <Label htmlFor="notification-rule-schema">
                    {t("workspace.notifications.rules.schemaId")}
                  </Label>
                  <Select
                    value={
                      ruleDraft.agentId && ruleDraft.schemaId
                        ? formScopeKey(ruleDraft.agentId, ruleDraft.schemaId)
                        : ""
                    }
                    onValueChange={(scopeKey) => {
                      const schema = formSchemaOptionMap.get(scopeKey)
                      if (!schema) return
                      setRuleDraft({
                        ...ruleDraft,
                        agentId: schema.agentId,
                        schemaId: schema.schemaId,
                      })
                    }}
                  >
                    <SelectTrigger
                      id="notification-rule-schema"
                      aria-invalid={
                        hasUnconditionalRuleConflict ||
                        conditionalRuleRequiresSchema
                      }
                      aria-describedby={
                        hasUnconditionalRuleConflict ||
                        conditionalRuleRequiresSchema
                          ? "notification-rule-schema-help notification-rule-schema-error"
                          : "notification-rule-schema-help"
                      }
                    >
                      <SelectValue
                        placeholder={t(
                          "workspace.notifications.rules.schemaPlaceholder"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableNewFormSchemaOptions.map((schema) => (
                        <SelectItem
                          key={formScopeKey(schema.agentId, schema.schemaId)}
                          value={formScopeKey(schema.agentId, schema.schemaId)}
                        >
                          {formSchemaOptionName(schema)}
                        </SelectItem>
                      ))}
                      {availableNewFormSchemaOptions.length === 0 ? (
                        <SelectItem value="__no_forms__" disabled>
                          {t("workspace.notifications.rules.noForms")}
                        </SelectItem>
                      ) : null}
                    </SelectContent>
                  </Select>
                  <p
                    id="notification-rule-schema-help"
                    className="text-xs text-muted-foreground"
                  >
                    {t("workspace.notifications.rules.schemaHelp")}
                  </p>
                  {hasUnconditionalRuleConflict ? (
                    <p
                      id="notification-rule-schema-error"
                      role="alert"
                      className="text-xs text-destructive"
                    >
                      {t("workspace.notifications.rules.unconditionalConflict")}
                    </p>
                  ) : conditionalRuleRequiresSchema ? (
                    <p
                      id="notification-rule-schema-error"
                      role="alert"
                      className="text-xs text-destructive"
                    >
                      {t("workspace.notifications.rules.conditionRequired")}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {!ruleDraft.id && !isFormIndividualRuleDraft ? (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="notification-rule-enabled"
                    checked={ruleDraft.isEnabled}
                    onCheckedChange={(checked) =>
                      setRuleDraft({
                        ...ruleDraft,
                        isEnabled: checked === true,
                      })
                    }
                  />
                  <Label htmlFor="notification-rule-enabled">
                    {t("workspace.notifications.rules.enabled")}
                  </Label>
                </div>
              ) : null}
              {isFormIndividualRuleDraft ? (
                <div className="space-y-2">
                  <Label htmlFor="form-request-notification-mode">
                    {t("workspace.notifications.agentSettings.mode")}
                  </Label>
                  <Select
                    value={ruleDraft.isEnabled ? "custom" : "disabled"}
                    onValueChange={(mode) =>
                      setRuleDraft({
                        ...ruleDraft,
                        isEnabled: mode === "custom",
                      })
                    }
                  >
                    <SelectTrigger id="form-request-notification-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">
                        {t("workspace.notifications.rules.modes.custom")}
                      </SelectItem>
                      <SelectItem value="disabled">
                        {t("workspace.notifications.rules.modes.disabled")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {!isFormIndividualRuleDraft || isFormIndividualRuleCustom ? (
                <div className="space-y-2">
                  <Label>
                    {t("workspace.notifications.rules.destinations")}
                  </Label>
                  <DestinationSelector
                    destinations={compatibleRuleDestinations}
                    selectedIds={ruleDraft.destinationIds}
                    emptyLabel={t(
                      "workspace.notifications.rules.noCompatibleDestinations"
                    )}
                    readyLabel={t("workspace.notifications.ready")}
                    pendingLabel={t("workspace.notifications.pending")}
                    disabledLabel={t("workspace.notifications.disabled")}
                    verificationPendingLabel={t(
                      "workspace.notifications.destinations.verificationPending"
                    )}
                    verificationExpiredLabel={t(
                      "workspace.notifications.destinations.verificationExpired"
                    )}
                    groupLabels={{
                      selected: t(
                        "workspace.notifications.selector.selectedDestinations",
                        { count: selectedRuleDestinations.length }
                      ),
                      available: t(
                        "workspace.notifications.selector.availableDestinations",
                        {
                          count:
                            compatibleRuleDestinations.length -
                            selectedRuleDestinations.length,
                        }
                      ),
                      selectedEmpty: t(
                        "workspace.notifications.selector.selectedDestinationsEmpty"
                      ),
                    }}
                    onChange={(destinationIds) =>
                      setRuleDraft({
                        ...ruleDraft,
                        destinationIds,
                      })
                    }
                  />
                  {enabledRuleHasNoSelectedDestination ? (
                    <p
                      role="alert"
                      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
                    >
                      {t(
                        "workspace.notifications.rules.noSelectedDestinationWarning"
                      )}
                    </p>
                  ) : enabledRuleHasNoReadyDestination ? (
                    <p
                      role="alert"
                      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
                    >
                      {t(
                        "workspace.notifications.rules.noReadyDestinationWarning"
                      )}
                    </p>
                  ) : hasUnavailableSelectedDestination ? (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "workspace.notifications.rules.unavailableDestinationNote"
                      )}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="border-t bg-background pt-4">
            <Button variant="outline" onClick={() => setRuleDraft(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={
                isSaving ||
                !isRuleDraftDirty ||
                conditionalRuleRequiresSchema ||
                hasUnconditionalRuleConflict ||
                enabledRuleHasNoSelectedDestination ||
                enabledRuleHasNoReadyDestination
              }
              onClick={saveRule}
            >
              {t(
                isFormIndividualRuleDraft
                  ? "workspace.notifications.rules.saveFormSetting"
                  : "workspace.notifications.rules.save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={agentHandoffSettingDraft !== null}
        onOpenChange={(open) => !open && setAgentHandoffSettingDraft(null)}
      >
        <DialogContent className="max-h-[calc(100dvh-2rem)] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {agentHandoffSettingDraft?.isNew
                ? t("workspace.notifications.agentSettings.createTitle")
                : t("workspace.notifications.agentSettings.editTitle", {
                    agent: agentHandoffSettingDraft?.agentName || "",
                  })}
            </DialogTitle>
            <DialogDescription>
              {t(
                agentHandoffSettingDraft?.isNew
                  ? "workspace.notifications.agentSettings.createDescription"
                  : "workspace.notifications.agentSettings.editDescription"
              )}
            </DialogDescription>
          </DialogHeader>
          {agentHandoffSettingDraft ? (
            <div className="min-h-0 min-w-0 space-y-4 overflow-y-auto overscroll-contain pr-1 [scrollbar-gutter:stable]">
              {agentHandoffSettingDraft.isNew ? (
                <div className="space-y-2">
                  <Label htmlFor="agent-handoff-notification-agent">
                    {t("workspace.notifications.agentSettings.agentLabel")}
                  </Label>
                  <Select
                    value={agentHandoffSettingDraft.agentId}
                    onValueChange={selectAgentForHandoffSetting}
                  >
                    <SelectTrigger
                      id="agent-handoff-notification-agent"
                      aria-invalid={agentHandoffRequiresAgent}
                      aria-describedby={
                        agentHandoffRequiresAgent
                          ? "agent-handoff-notification-agent-help agent-handoff-notification-agent-error"
                          : "agent-handoff-notification-agent-help"
                      }
                    >
                      <SelectValue
                        placeholder={t(
                          "workspace.notifications.agentSettings.agentPlaceholder"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {agentsUsingWorkspaceDefault.map((setting) => (
                        <SelectItem
                          key={setting.agentId}
                          value={setting.agentId}
                        >
                          {setting.agentName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p
                    id="agent-handoff-notification-agent-help"
                    className="text-xs text-muted-foreground"
                  >
                    {t("workspace.notifications.agentSettings.agentHelp")}
                  </p>
                  {agentHandoffRequiresAgent ? (
                    <p
                      id="agent-handoff-notification-agent-error"
                      role="alert"
                      className="text-xs text-destructive"
                    >
                      {t("workspace.notifications.agentSettings.agentRequired")}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="agent-handoff-notification-mode">
                  {t("workspace.notifications.agentSettings.mode")}
                </Label>
                <Select
                  value={agentHandoffSettingDraft.mode}
                  onValueChange={(mode: HandoffNotificationOverrideMode) =>
                    setAgentHandoffSettingDraft({
                      ...agentHandoffSettingDraft,
                      mode,
                    })
                  }
                >
                  <SelectTrigger id="agent-handoff-notification-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">
                      {t("workspace.notifications.agentSettings.modes.custom")}
                    </SelectItem>
                    <SelectItem value="disabled">
                      {t(
                        "workspace.notifications.agentSettings.modes.disabled"
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {agentHandoffSettingDraft.mode === "custom" ? (
                <div className="space-y-2">
                  <Label>
                    {t("workspace.notifications.agentSettings.destinations")}
                  </Label>
                  <DestinationSelector
                    destinations={handoffDestinations}
                    selectedIds={agentHandoffSettingDraft.destinationIds}
                    emptyLabel={t(
                      "workspace.notifications.agentSettings.noDestinations"
                    )}
                    readyLabel={t("workspace.notifications.ready")}
                    pendingLabel={t("workspace.notifications.pending")}
                    disabledLabel={t("workspace.notifications.disabled")}
                    verificationPendingLabel={t(
                      "workspace.notifications.destinations.verificationPending"
                    )}
                    verificationExpiredLabel={t(
                      "workspace.notifications.destinations.verificationExpired"
                    )}
                    groupLabels={{
                      selected: t(
                        "workspace.notifications.selector.selectedDestinations",
                        {
                          count: selectedAgentHandoffDestinations.length,
                        }
                      ),
                      available: t(
                        "workspace.notifications.selector.availableDestinations",
                        {
                          count:
                            handoffDestinations.length -
                            selectedAgentHandoffDestinations.length,
                        }
                      ),
                      selectedEmpty: t(
                        "workspace.notifications.selector.selectedDestinationsEmpty"
                      ),
                    }}
                    onChange={(destinationIds) =>
                      setAgentHandoffSettingDraft({
                        ...agentHandoffSettingDraft,
                        destinationIds,
                      })
                    }
                  />
                  {agentCustomHasNoReadyDestination ? (
                    <p
                      role="alert"
                      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
                    >
                      {t(
                        "workspace.notifications.agentSettings.noReadyDestinationWarning"
                      )}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="border-t bg-background pt-4">
            <Button
              variant="outline"
              onClick={() => setAgentHandoffSettingDraft(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={
                isSaving ||
                !isAgentHandoffSettingDraftDirty ||
                agentHandoffRequiresAgent ||
                agentCustomHasNoReadyDestination
              }
              onClick={saveAgentHandoffSetting}
            >
              {t("workspace.notifications.agentSettings.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NotificationConfirmDialog
        open={confirmationRequest !== null}
        title={confirmationRequest?.title ?? ""}
        description={confirmationRequest?.description ?? ""}
        cancelText={t("common.cancel")}
        confirmText={confirmationRequest?.confirmText ?? ""}
        destructive={confirmationRequest?.destructive}
        onCancel={() => setConfirmationRequest(null)}
        onConfirm={() => {
          if (!confirmationRequest) return
          const { onConfirm } = confirmationRequest
          setConfirmationRequest(null)
          onConfirm()
        }}
      />
    </div>
  )
}

export default NotificationsTab
